export interface HomeGroupEntryInput {
  hasSiteRestrictions: boolean;
  accessibleCompanyCount: number;
  accessibleSiteCount: number;
  isGroupAdmin: boolean;
}

/**
 * CR-7411: a site restricted customer group admin holding exactly one site.
 * The site hierarchy collapses one division to a flat list, so without the
 * home group entry such a user (Atlas Copco: one Group, one Site) has no way
 * to select group level at all.
 */
export function isSingleSiteGroupAdmin({
  hasSiteRestrictions,
  accessibleSiteCount,
  isGroupAdmin,
}: HomeGroupEntryInput): boolean {
  return hasSiteRestrictions && isGroupAdmin && accessibleSiteCount === 1;
}

/**
 * Whether the TopBar offers the home group rollup entry (CR-4541 / CR-5315),
 * before the division header dedup is applied.
 *
 * A site restricted user normally needs more than one site to have a rollup
 * to choose. CR-7411 adds the single site group admin (see above). Selection
 * is accepted server side because the home company sits in that group, or
 * one of its ancestors (validate_current_group).
 */
export function isHomeGroupEntryVisible(input: HomeGroupEntryInput): boolean {
  const { hasSiteRestrictions, accessibleCompanyCount, accessibleSiteCount } = input;
  if (!hasSiteRestrictions) return accessibleCompanyCount > 1;
  if (accessibleSiteCount > 1) return true;
  return isSingleSiteGroupAdmin(input);
}

export interface GroupRef {
  id: number;
  name: string;
  root_group?: number;
  root_group_name?: string;
}

export interface RootTargetInput {
  hasSiteRestrictions: boolean;
  isGroupAdmin: boolean;
  /** Distinct groups the user's sites sit in, and sites with no group. */
  distinctDivisionCount: number;
  ungroupedCount: number;
}

/**
 * CR-7411: whether the home entry should select the tenant's top Group rather
 * than the home group. A group admin's authority covers the whole tenant
 * (is_customer_group_admin checks the ROOT tree), so when all their sites sit
 * in one group the entry offers the top Group, not that division. With sites
 * across several divisions the CR-6086 root entry already offers the Group,
 * so the home entry keeps the home group and nothing appears twice.
 */
export function targetsRootGroup({
  hasSiteRestrictions,
  isGroupAdmin,
  distinctDivisionCount,
  ungroupedCount,
}: RootTargetInput): boolean {
  return hasSiteRestrictions && isGroupAdmin && distinctDivisionCount <= 1 && ungroupedCount === 0;
}

/**
 * CR-7411: which group the entry selects: the top Group when targetsRootGroup
 * says so and the root is known, otherwise the home group, unchanged.
 */
export function homeGroupEntryTarget(
  group: GroupRef,
  useRootGroup: boolean,
): { id: number; name: string } {
  if (useRootGroup && group.root_group && group.root_group_name) {
    return { id: group.root_group, name: group.root_group_name };
  }
  return { id: group.id, name: group.name };
}
